import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./Bird.scss";
import { MapContext } from "../../../../context/MapContext";
import useQueryString from "../../../../hooks/useQueryString";
import {
  BirdConfig,
  ReferenceConfig,
  refLayersToInclude,
} from "../WildLifeTourismConfig";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import QueryTask from "@arcgis/core/tasks/QueryTask";
import Query from "@arcgis/core/tasks/support/Query";
import BirdStats from "./BirdStats/BirdStats";
import ReferenceLayers from "../../../ReferenceLayers/ReferenceLayers";
import utils from "../../../../helpers/utils";
import References from "../../../References/References";
import { Box, Button, Card } from "@material-ui/core";

function Bird(props) {
  const { selectedGeometry, isGeoScaled, geoSelections } = props;
  const [mapObject] = useContext(MapContext);

  let [isWeighted, setIsWeighted] = useQueryString("bIsWeighted", false);
  const [stats, setStats] = useState({});
  const [refLayersEnabled, setRefLayersEnabled] = useQueryString(
    "bRefLayers",
    []
  );

  /// fix is weighted boolean
  if (isWeighted === "false") {
    isWeighted = false;
  }
  if (isWeighted === "true") {
    isWeighted = true;
  }

  /**
   * Every time this route is accessed, make sure the EEZ layer reflects the correct boundaries
   */
  useEffect(() => {
    const eez = mapObject?.map.findLayerById("eez");
    if (eez) {
      eez.definitionExpression = `Dissolve in (${geoSelections
        .map((c) => "'" + utils.getCountryForEEZ(c) + "'")
        .join(",")})`;
    }
  }, [mapObject, geoSelections]);

  /**
   * Function that looks up layer object from config file to use based on current
   * app selections (selected geometry)
   */
  const getLayerForSelections = (isWeighted) => {
    const dataSet = BirdConfig.data;
    if (isWeighted) {
      return dataSet.find((service) => service.name === "Species Importance");
    }
    return dataSet.find((service) => service.name === "Birder Footfall");
  };

  /**
   * Only one model layer is active at a time. Memoize it and update when user selections change
   */
  const layer = useMemo(() => {
    const dataLayer = getLayerForSelections(isWeighted);
    return new FeatureLayer({ url: dataLayer.url, title: dataLayer.name });
  }, [isWeighted]);

  /**
   * When the memoized layer object updates, we need to remove the current layer
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.remove(layer);
    }
  }, [layer, mapObject?.map]);

  /**
   * Second effect declared to add the new layer. Runs after first efect that removes. Has callback for removing on component unmount
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.add(layer);

      return () => {
        mapObject?.map.remove(layer);
      };
    }
  }, [mapObject?.map, layer]);

  /**
   * On component mount, fetch the stats table and prepare a query tasks to execute queries on
   */
  const queryTable = useMemo(() => {
    const crtTable = BirdConfig.data.find(
      (crt) => crt.name === "Statistics Table"
    );
    return new QueryTask({ url: crtTable.url });
  }, []);

  /**
   * Fetch the stats for the currrent app and set them in state.
   */
  useEffect(() => {
    const query = new Query();
    query.returnGeometry = false;
    query.outFields = ["*"];
    query.where = "App = 'Birdwatching'";

    queryTable.execute(query).then((results) => {
      const newStats = {};
      results.features.forEach((feature) => {
        newStats[feature.attributes.Statistic] = feature.attributes;
      });
      setStats(newStats);
    });
  }, [queryTable, setStats]);

  /**
   * Simple handler for when the toggles between weighted and un-weighted model. Update in state
   */
  const handleWeightedChange = (isWeightedInput) => {
    if (isWeightedInput === isWeighted) {
      return;
    }
    setIsWeighted(isWeightedInput);
  };

  /**
   * Create a callback to set layer def expression when selected geometry changes and geo is scaled
   * Uses more "react-y" pattern to accomplish same type of task as getLayerForSelections
   */
  const setLayerDefinition = useCallback(() => {
    if (isGeoScaled && selectedGeometry !== "CROP Countries") {
      layer.definitionExpression = `Dissolve = '${selectedGeometry.toUpperCase()}'`;
    } else {
      layer.definitionExpression = "1=1";
    }
    layer.refresh();
  }, [isGeoScaled, selectedGeometry, layer]);

  /**
   * When the set layer definition call back changes, we should call it
   * Not sure if best pattern for useCallback... trying it out for first time
   */
  useEffect(() => {
    setLayerDefinition();
  }, [setLayerDefinition, layer]);

  /**
   * Toggles the visibility of a reference layer by adding/removing from an array that tracks currently visible ref layers
   */
  const handleRefLayerToggle = (layer) => {
    const newRefLayersEnabled = [...refLayersEnabled];
    if (newRefLayersEnabled.includes(layer)) {
      newRefLayersEnabled.splice(
        newRefLayersEnabled.findIndex((refLayer) => refLayer === layer),
        1
      );
    } else {
      newRefLayersEnabled.push(layer);
    }
    setRefLayersEnabled(newRefLayersEnabled);
  };

  return (
    <div className="birdWatching">
      <div className="quickStats">
        <h3>Quick Stats for {selectedGeometry}</h3>
        <Box className="switch-wrapper">
          <Card variant="outlined" className="switch-card">
            <Button
              onClick={() => handleWeightedChange(false)}
              className={isWeighted ? "switchButton" : "switchButton active"}
            >
              Birder Footfall
            </Button>
            <Button
              onClick={() => handleWeightedChange(true)}
              className={isWeighted ? "active switchButton" : "switchButton"}
            >
              Species Importance
            </Button>
          </Card>
        </Box>
        <div className="subInfo">
          {!isWeighted &&
            'Estimate of birdwatching effort, based on data uploaded by users of the internationally popular eBird app. Numbers represent birdwatching intensity, with the score being the sum of "observer-day" uploads per 500m grid cell (ie. capturing only one observation per observer per day per cell).'}
          {isWeighted &&
            "Estimate of the importance of each grid cell unit to birdwatching based on the species importance score of birds seen in this area (based on eBird observation data). The species importance score is a sum of 4 metrics applied to each species (endemism, threat status, rarity, and charisma); this score is multipled by the number of observations of this species, and weighted observations for all species are summed within a grid cell."}
        </div>
        <BirdStats stats={stats} selectedGeometry={selectedGeometry} />
      </div>
      <div className="otherMaps">
        <h3>Additional Map Layers:</h3>
        <ReferenceLayers
          layersToInclude={refLayersToInclude.bird}
          layersEnabled={refLayersEnabled}
          handleLayerToggle={handleRefLayerToggle}
          countries={geoSelections}
        />
      </div>
      {ReferenceConfig && (
        <div className="references-wrapper">
          <h3>References</h3>
          <References refs={ReferenceConfig.bird} />
        </div>
      )}
    </div>
  );
}

export default Bird;
